import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Checkbox,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ThemeContext } from "../../../../../../../../context/ThemeContextProvider";
import { useOrder } from "../../../../../../../../context/OrderCreator/TablesContext/OrderContext";

const DivideOrderModal = ({ open, onClose, orderId, items = [], onSuccess }) => {
  const { mode } = useContext(ThemeContext);
  const { divideOrder } = useOrder();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md")); // full screen on small tablets/phones
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [subItems, setSubItems] = useState([]);

  useEffect(() => {
    // initialize/reset subItems when items or open change
    if (Array.isArray(items) && items.length > 0) {
      setSubItems(
        items.map((it) => ({
          ...it,
          selected: false,
          quantity: Number(it.quantity ?? 1),
        }))
      );
    } else {
      setSubItems([]);
    }
  }, [items, open]);

  const handleQuantityChange = (itemId, value) => {
    setSubItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, quantity: Number(value) } : item
      )
    );
  };

  const handleSelect = (itemId) => {
    setSubItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleSubmit = async () => {
    const selectedItems = subItems
      .filter((item) => item.selected && Number(item.quantity) > 0)
      .map((item) => ({
        item_id: item.id,
        quantity: parseInt(item.quantity, 10),
      }));

    if (selectedItems.length === 0) {
      alert("❗ اختر على الأقل صنف واحد.");
      return;
    }

    try {
      await divideOrder(orderId, selectedItems);

      alert("✅ تم تقسيم الطلب بنجاح");

      if (typeof window.refreshOrderDetails === "function") {
        window.refreshOrderDetails(orderId);
      }

      onClose();
      onSuccess?.();
    } catch (err) {
      alert(err.message || "❌ حدث خطأ أثناء تنفيذ الطلب.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          bgcolor: mode === "dark" ? "#232334" : "#fff",
          color: mode === "dark" ? "#fff" : "#232334",
          borderRadius: fullScreen ? 0 : 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: { xs: 18, sm: 22 },
          textAlign: "center",
          color: "#EA7C69",
          bgcolor: mode === "dark" ? "#181824" : "#fff",
          py: { xs: 1, sm: 2 },
        }}
      >
        🧩 تقسيم الطلب
      </DialogTitle>
      <DialogContent
        sx={{
          bgcolor: mode === "dark" ? "#232334" : "#fff",
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          maxHeight: fullScreen ? "calc(100vh - 160px)" : "auto",
          overflowY: fullScreen ? "auto" : "visible",
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
        tabIndex={0}
      >
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            {subItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Paper
                  elevation={item.selected ? 6 : 1}
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    border: item.selected
                      ? "2px solid #EA7C69"
                      : mode === "dark"
                      ? "1px solid #444"
                      : "1px solid #eee",
                    borderRadius: 2,
                    background: item.selected
                      ? mode === "dark"
                        ? "#2c2230"
                        : "#fff7f3"
                      : mode === "dark"
                      ? "#232334"
                      : "#fafafa",
                    transition: "all 0.2s",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                  }}
                >
                  <Checkbox
                    checked={item.selected}
                    onChange={() => handleSelect(item.id)}
                    sx={{
                      color: "#EA7C69",
                      "&.Mui-checked": { color: "#EA7C69" },
                    }}
                    size={isXs ? "small" : "medium"}
                    inputProps={{ "aria-label": `تحديد ${item.product_name}` }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: mode === "dark" ? "#fff" : "#232334",
                        fontSize: { xs: 14, sm: 15 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.product_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: mode === "dark" ? "#aaa" : "#888",
                        mt: 0.5,
                      }}
                    >
                      المتاح: {item.available_quantity ?? item.quantity}
                    </Typography>
                  </Box>
                  <TextField
                    type="number"
                    size={isXs ? "small" : "medium"}
                    label="الكمية"
                    value={item.quantity}
                    onChange={(e) =>
                      handleQuantityChange(item.id, e.target.value)
                    }
                    disabled={!item.selected}
                    inputProps={{
                      min: 1,
                      style: { width: 80, textAlign: "center" },
                    }}
                    sx={{
                      mt: { xs: 1, sm: 0 },
                      "& .MuiInputBase-root": {
                        borderRadius: 2,
                        bgcolor: mode === "dark" ? "#181824" : "#fff",
                        color: mode === "dark" ? "#fff" : "#232334",
                      },
                      "& .MuiInputLabel-root": {
                        color: mode === "dark" ? "#aaa" : undefined,
                      },
                    }}
                    fullWidth={isXs}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          pb: 2,
          bgcolor: mode === "dark" ? "#232334" : "#fff",
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: { xs: 1, sm: 2 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          sx={{
            minWidth: 100,
            borderColor: mode === "dark" ? "#888" : undefined,
            color: mode === "dark" ? "#fff" : undefined,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            bgcolor: "#EA7C69",
            color: "#fff",
            minWidth: 120,
            fontWeight: 700,
            "&:hover": { bgcolor: "#d45d4c" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          تنفيذ
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DivideOrderModal;
